import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@lumika/ui";
import { adminApi } from "../api/admin";
import { BillingView, defaultBillingRange } from "../components/BillingView";

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Platform-wide billing — Lumika ops view. Shows the AI ledger
 * aggregated across every dealership, the Twilio total pulled from
 * `twilio_usage_records`, and a Top-N table by combined spend so
 * outliers are easy to spot.
 */
function toLocalDay(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function presetRange(days: number): { start: string; end: string } {
  const today = new Date();
  return {
    start: toLocalDay(new Date(today.getTime() - days * 86_400_000)),
    end: toLocalDay(today),
  };
}

export function PlatformBillingPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState(defaultBillingRange);
  const q = useQuery({
    queryKey: ["admin", "billing", "platform", range.start, range.end],
    queryFn: () => adminApi.platformBilling(range),
  });

  if (q.isLoading) return <PageLoading message="Loading platform billing…" />;
  if (q.isError) {
    return (
      <PageError
        title="Couldn't load platform billing"
        description="Try refreshing the page."
      />
    );
  }
  if (!q.data) return null;

  const coverage = q.data?.twilioCoverage;
  const coverageNote =
    coverage && (coverage.minDay > range.start || coverage.maxDay < range.end)
      ? `Twilio costs synced ${coverage.minDay}–${coverage.maxDay}; earlier days may be incomplete.`
      : undefined;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Billing"
        title="Platform"
        description={`Cross-tenant AI + Twilio totals. Window: ${range.start} → ${range.end}.`}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="date"
          value={range.start}
          onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
          className="w-40"
        />
        <span className="text-sm text-muted-foreground">to</span>
        <Input
          type="date"
          value={range.end}
          onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
          className="w-40"
        />
        <div className="flex gap-1">
          {([7, 30, 90] as const).map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => setRange(presetRange(days))}
              className="rounded border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      <Section
        title="Top dealerships by spend"
        description="Sorted by combined AI + Twilio cost in this window."
      >
        {q.data.topDealerships.length === 0 ? (
          <p className="text-sm text-muted-foreground">No usage in this range.</p>
        ) : (
          <>
            {/* Desktop: data table. */}
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dealership</TableHead>
                    <TableHead className="text-right">AI</TableHead>
                    <TableHead className="text-right">Twilio</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {q.data.topDealerships.map((d) => (
                    <TableRow key={d.dealershipId}>
                      <TableCell className="font-medium">
                        <Link
                          to={`/dealerships/${d.dealershipId}/billing`}
                          className="hover:underline"
                        >
                          {d.dealershipName}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{usd.format(d.aiUsd)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usd.format(d.twilioUsd)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {usd.format(d.totalUsd)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            {/* Mobile: condensed touch list with the same spend hierarchy. */}
            <MobileList>
              {q.data.topDealerships.map((d) => (
                <MobileListRow
                  key={d.dealershipId}
                  onClick={() => navigate(`/dealerships/${d.dealershipId}/billing`)}
                  primary={d.dealershipName}
                  secondary={
                    <span className="tabular-nums">
                      AI {usd.format(d.aiUsd)} · Twilio {usd.format(d.twilioUsd)}
                    </span>
                  }
                  trailing={
                    <span className="tabular-nums font-semibold">
                      {usd.format(d.totalUsd)}
                    </span>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      <BillingView
        ai={q.data.ai}
        totals={{
          aiUsd: q.data.totals.aiUsd,
          twilioUsd: q.data.totals.twilioUsd,
          totalUsd: q.data.totals.totalUsd,
        }}
        range={range}
        unattributedTwilioUsd={q.data.unattributedTwilioUsd}
        coverageNote={coverageNote}
      />
    </div>
  );
}
